import {Database} from "@docid/db/types";
import {PostgrestError, AuthError, SupabaseClient} from "@supabase/supabase-js";
import {
    AccountResponse,
    AccountByIdRequest,
    accountByIdRequest,
    AccountByEmailRequest,
    accountByEmailRequest,
    AccountFetchPaginatedRequest,
    accountFetchPaginatedRequest,
    accountUpdateRequest,
    AccountUpdateRequest,
    accountUpdateSignupGeneralRequest,
    AccountUpdateSignupGeneralRequest,
    accountUpdatePersonalDetailsRequest,
    AccountUpdatePersonalDetailsRequest,
    accountUpdateBusinessDetailsRequest,
    AccountUpdateBusinessDetailsRequest,
    accountUpdateDisciplineRequest,
    AccountUpdateDisciplineRequest,
    accountUpdateInstitutionRequest,
    AccountUpdateInstitutionRequest,
    accountUpdateProfessionRequest,
    AccountUpdateProfessionRequest,
    accountUpdateSubjectRequest,
    AccountUpdateSubjectRequest,
    accountDeleteRequest,
    AccountDeleteRequest
} from "../types/accounts";

export const fetchById = async(input: AccountByIdRequest, supabase: SupabaseClient) :
    Promise<{data: AccountResponse | null, error: PostgrestError | null }> => {

    const parsedInput = accountByIdRequest.parse(input);

    const { data, error } = await supabase
        .from("accounts")
        .select("id, gender, title, firstname, lastname, street, street_no, postcode, city, country, email, phone, status:accounts_status!inner(*), profession:profession_id(*), discipline:discipline_id(*), institution:institution_id(*), subject:subject_id(*), updated_at, created_at")
        .eq("id", parsedInput.id)
        .maybeSingle();

    return {
        data: data as unknown as AccountResponse,
        error: error,
    }

}

export const fetchAccountByEmail = async (input: AccountByEmailRequest, supabase: SupabaseClient) :
    Promise<{ data: Pick<AccountResponse, "id" | "email"> | null; error: PostgrestError | null }> => {

    const parsedInput = accountByEmailRequest.parse(input)

    const { data, error } = await supabase
        .from("accounts")
        .select("id, email")
        .eq("email", parsedInput.email)
        .limit(1)
        .maybeSingle();

    return {
        data: data as unknown as Pick<AccountResponse, "id" | "email">,
        error
    }

}

export const fetchPaginatedAccounts = async(input: AccountFetchPaginatedRequest, supabase: SupabaseClient) :
    Promise<{data: AccountResponse[], error: PostgrestError | null, total: number | null }> => {

    const parsedInput = accountFetchPaginatedRequest.parse(input);

    let query = await supabase
        .from("accounts")
        .select("id, gender, title, firstname, lastname, street, street_no, postcode, city, country, email, phone, status:accounts_status!inner(*), profession:profession_id(*), discipline:discipline_id(*), institution:institution_id(*), subject:subject_id(*), updated_at, created_at", { count: 'exact' })
        .order("created_at", { ascending: false })
        .range(parsedInput.rangeStart, parsedInput.rangeEnd);

    const { data, error, count} = await query;

    return {
        data: data as unknown as AccountResponse[],
        error: error,
        total: count,
    }

}

export const updateAccount = async(input: AccountUpdateRequest, supabase: SupabaseClient) :
    Promise<{status: number, error: PostgrestError | null}> => {

    const parsedInput = accountUpdateRequest.parse(input);

    const { error, status } = await supabase
        .from("accounts").update(parsedInput.data)
        .eq("id", parsedInput.id);

    return {
        status: status,
        error: error,
    }

}

export const updateSignupGeneral = async(input: AccountUpdateSignupGeneralRequest, supabase: SupabaseClient) :
    Promise<{status: number, error: PostgrestError | null}> => {

    const parsedInput = accountUpdateSignupGeneralRequest.parse(input);

    const { error, status } = await supabase
        .from("accounts").update(parsedInput.data)
        .eq("id", parsedInput.id);

    return {
        status: status,
        error: error,
    }

}

export const updatePersonalDetails = async(input: AccountUpdatePersonalDetailsRequest, supabase: SupabaseClient) :
    Promise<{status: number, error: PostgrestError | null}> => {

    const parsedInput = accountUpdatePersonalDetailsRequest.parse(input);

    const { error, status } = await supabase
        .from("accounts").update(parsedInput.data)
        .eq("id", parsedInput.id);

    return {
        status: status,
        error: error,
    }

}

export const updateBusinessDetails = async(input: AccountUpdateBusinessDetailsRequest, supabase: SupabaseClient) :
    Promise<{status: number, error: PostgrestError | null}> => {

    const parsedInput = accountUpdateBusinessDetailsRequest.parse(input);

    const { error, status } = await supabase
        .from("accounts").update(parsedInput.data)
        .eq("id", parsedInput.id);

    return {
        status: status,
        error: error,
    }

}

export const updateProfession = async(input: AccountUpdateProfessionRequest, supabase: SupabaseClient) :
    Promise<{status: number, error: PostgrestError | null}> => {

    const parsedInput = accountUpdateProfessionRequest.parse(input);

    const { error, status } = await supabase
        .from("accounts").update({
            ...parsedInput.data,
            discipline_id: null,
            institution_id: null,
            subject_id: null,
        })
        .eq("id", parsedInput.id);

    return {
        status: status,
        error: error,
    }

}

export const updateDiscipline = async(input: AccountUpdateDisciplineRequest, supabase: SupabaseClient<Database>) :
    Promise<{status: number, error: PostgrestError | null}> => {

    const parsedInput = accountUpdateDisciplineRequest.parse(input);

    const { error, status } = await supabase
        .from("accounts").update(parsedInput.data)
        .eq("id", parsedInput.id);

    return {
        status: status,
        error: error,
    }

}

export const updateInstitution = async(input: AccountUpdateInstitutionRequest, supabase: SupabaseClient) :
    Promise<{status: number, error: PostgrestError | null}> => {

    const parsedInput = accountUpdateInstitutionRequest.parse(input);

    const { error, status } = await supabase
        .from("accounts").update(parsedInput.data)
        .eq("id", parsedInput.id);

    return {
        status: status,
        error: error,
    }

}

export const updateSubject = async(input: AccountUpdateSubjectRequest, supabase: SupabaseClient) :
    Promise<{status: number, error: PostgrestError | null}> => {

    const parsedInput = accountUpdateSubjectRequest.parse(input);

    const { error, status } = await supabase
        .from("accounts").update(parsedInput.data)
        .eq("id", parsedInput.id);

    return {
        status: status,
        error: error,
    }

}

export const deleteAccount = async(input: AccountDeleteRequest, supabase: SupabaseClient) :
    Promise<{status: number, error: AuthError | null}> => {

    const parsedInput = accountDeleteRequest.parse(input);

    const { error } = await supabase.auth.admin.deleteUser(
        parsedInput.id
    )

    if(error) {
        return {
            status: 500,
            error: error,
        }
    }

    return {
        status: 200,
        error: null,
    }

}
